import { call, put, select } from 'redux-saga/effects';
import serviceLocator, { SSAP_SVC } from '../../APIs/common/service-locator';
import {
    cachePreviousIMI,
    cacheIMI,
    receiveIMIFailure,
    receiveIMISuccess,
    startReceiveIMI,
    updateIMI,
    updatePreviousIMI
} from '../../actions/ssap/ssapActionCreators';

import {
    getApplicantGuids,
    getApplicationId,
    getCachedData,
    getPreviousApplicantGuids,
    getPreviousApplicationId,
    getActivePageName
} from '../../reducers/appReducer';

import { isEqual } from '../../utils/miscellaneous/objectHelpers';

import { PAGES } from '../../constants/ssap/flows/pagesTypes';

export function* executeSagaCallbacks(callbacks, status) {
    if (callbacks) {
        const {
            updateStatus,
            toggleExtraDataStatus,
            togglePageReadiness,
        } = callbacks;

        if (typeof updateStatus === 'function') {
            yield call(updateStatus, status);
        }

        if (typeof toggleExtraDataStatus === 'function') {
            yield call(toggleExtraDataStatus, status === 'started' ? false : true);
        }

        if (typeof togglePageReadiness === 'function') {
            yield call(togglePageReadiness, status === 'started' ? false : true);
        }
    }
}

export const checkCachedIMI = (guids, cachedIMI) => {
    return isEqual(guids, Object.keys(cachedIMI), true); // Here we need to make sure it works if they have different order
};

export const isResponseValid = (response, guids) => {
    if (!response) {
        return false;
    }

    const guidsFromResponse = response.map(r => r.applicantGuid);

    return isEqual(guidsFromResponse, guids, true);
};

export const makeIMIPayloadFromResponse = data => {
    return data
        .reduce(
            (immigrantInfo, applicant) => ({
                ...immigrantInfo,
                // TODO: Review next line: it creates applicantGuid in citizenshipImmigrationStatus.citizenshipDocument
                // and it is not required
                [applicant.applicantGuid]: applicant
            }),
            {}
        );
};

export function* receiveImmigrationInfoSaga(action) {
    try {
        const { callbacks } = action;

        const cachedData = yield select(getCachedData);
        const currentApplicationId = yield select(getApplicationId);
        const currentGuids = yield select(getApplicantGuids);
        const previousApplicationId = yield select(getPreviousApplicationId);
        const previousGuids = yield select(getPreviousApplicantGuids);
        const sagaCaller = yield select(getActivePageName);

        const isCitizenImmigrationStatusPage = sagaCaller === PAGES.CITIZENSHIP_IMMIGRATION_STATUS_PAGE;
        const currentIMIAlreadyCached = yield call(checkCachedIMI, currentGuids, cachedData.immigrationInfo);
        const previousIMIAlreadyCached = yield call(checkCachedIMI, previousGuids, cachedData.previousImmigrationInfo);

        switch (sagaCaller) {
            case PAGES.CITIZENSHIP_IMMIGRATION_STATUS_PAGE:
            case PAGES.FAMILY_AND_HOUSEHOLD_SUMMARY_PAGE:
            case PAGES.EXTRA_FINAL_APPLICATION_REVIEW_PAGE:

                if (currentIMIAlreadyCached) {
                    yield put(updateIMI(cachedData.immigrationInfo, true));
                    yield call(executeSagaCallbacks, callbacks, 'completed');
                    return;
                } else {
                    if (isCitizenImmigrationStatusPage) {
                        yield put(startReceiveIMI());
                    } else {
                        yield call(executeSagaCallbacks, callbacks, 'started');
                    }

                    const ssapService = yield call(serviceLocator, SSAP_SVC);

                    const response = yield call(
                        [
                            ssapService,
                            ssapService.receiveImmigrationInfo
                        ],
                        { ssapApplicationId: currentApplicationId }
                    );

                    if (isResponseValid(response, currentGuids)) {
                        const imiData = makeIMIPayloadFromResponse(response);
                        yield put(updateIMI(imiData));
                        yield put(cacheIMI(imiData));
                        yield call(executeSagaCallbacks, callbacks, 'completed');

                        if (isCitizenImmigrationStatusPage) {
                            yield put(receiveIMISuccess());
                        }

                        return;
                    } else {
                        if (isCitizenImmigrationStatusPage) {
                            yield put(receiveIMIFailure()); // critical failure, hard stop
                        } else {
                            yield call(executeSagaCallbacks, callbacks, 'failed'); // here we will fail silently
                        }
                    }
                }
                break;

            case PAGES.FINAL_APPLICATION_REVIEW_PAGE:
                if (currentIMIAlreadyCached && previousIMIAlreadyCached) {
                    yield put(updateIMI(cachedData.immigrationInfo, true));
                    yield put(updatePreviousIMI(cachedData.previousImmigrationInfo));
                    yield call(executeSagaCallbacks, callbacks, 'completed');
                    return;
                } else {
                    if (currentIMIAlreadyCached) {
                        yield call(executeSagaCallbacks, callbacks, 'started');

                        // We have current data cached, but not previous ones
                        const ssapService = yield call(serviceLocator, SSAP_SVC);

                        const response = yield call(
                            [
                                ssapService,
                                ssapService.receiveImmigrationInfo
                            ],
                            { ssapApplicationId: previousApplicationId }
                        );

                        if (isResponseValid(response, previousGuids)) {
                            const previousIMIdata = makeIMIPayloadFromResponse(response);

                            yield put(updateIMI(cachedData.immigrationInfo));
                            yield put(updatePreviousIMI(previousIMIdata));
                            yield put(cachePreviousIMI(previousIMIdata));    
                            yield call(executeSagaCallbacks, callbacks, 'completed');
                            return;
                        } else {
                            yield call(executeSagaCallbacks, callbacks, 'failed'); // here we will fail silently
                        }
                    }

                    if (previousIMIAlreadyCached) {
                        yield call(executeSagaCallbacks, callbacks, 'started');

                        // We have previous data cached, but not current ones
                        const ssapService = yield call(serviceLocator, SSAP_SVC);

                        const response = yield call(
                            [
                                ssapService,
                                ssapService.receiveImmigrationInfo
                            ],
                            { ssapApplicationId: currentApplicationId }
                        );

                        if (isResponseValid(response, currentGuids)) {
                            const imiData = makeIMIPayloadFromResponse(response);

                            yield put(updateIMI(imiData));
                            yield put(updatePreviousIMI(cachedData.previousImmigrationInfo));
                            yield put(cacheIMI(imiData));                    
                            yield call(executeSagaCallbacks, callbacks, 'completed');
                            return;
                        } else {
                            yield call(executeSagaCallbacks, callbacks, 'failed'); // here we will fail silently
                        }
                    }

                    if (!currentIMIAlreadyCached && !previousIMIAlreadyCached) {
                        yield call(executeSagaCallbacks, callbacks, 'started');

                        // We do not have any cached values, so we need to fetch both current and previous data
                        const ssapService = yield call(serviceLocator, SSAP_SVC);

                        const [currentResponse, previousResponse] = yield Promise.all(
                            [
                                yield call(
                                    [
                                        ssapService,
                                        ssapService.receiveImmigrationInfo
                                    ],
                                    { ssapApplicationId: currentApplicationId }
                                ),

                                yield call(
                                    [
                                        ssapService,
                                        ssapService.receiveImmigrationInfo
                                    ],
                                    { ssapApplicationId: previousApplicationId }
                                )
                            ]
                        );

                        const isCurrentResponseValid = isResponseValid(currentResponse, currentGuids);
                        const isPreviousResponseValid = isResponseValid(previousResponse, previousGuids);

                        if (isCurrentResponseValid && isPreviousResponseValid) {
                            const imiData = makeIMIPayloadFromResponse(currentResponse);
                            const previousIMI = makeIMIPayloadFromResponse(previousResponse);

                            yield put(updateIMI(imiData));
                            yield put(updatePreviousIMI(previousIMI));
                            yield put(cacheIMI(imiData));
                            yield put(cachePreviousIMI(previousIMI));     
                            yield call(executeSagaCallbacks, callbacks, 'completed');
                            return;
                        } else {
                            yield call(executeSagaCallbacks, callbacks, 'failed'); // here we will fail silently
                        }
                    }
                }
                break;

        }
    } catch (e) {
        const { console, console: { error } } = global;
        yield call([console, error], e);
    }
}